import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { getDoc, doc } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { WebsiteSettings } from '@/lib/firebase';
import { Calendar, Award, Users, Star } from 'lucide-react';
import Header from '@/components/Header';
import PageTitle from '@/components/PageTitle';
import { Card, CardContent } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { Clock, MapPin, Phone } from 'lucide-react';

const About = () => {
  const [settings, setSettings] = useState<WebsiteSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const docRef = doc(db, 'settings', 'website');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        setSettings(docSnap.data() as WebsiteSettings);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-20">
      {/* Hero Section */}
      <section className="relative h-[50vh] flex items-center">
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-black/50 z-10"></div>
        <img
          src={settings?.doctor.image || '/doctor-placeholder.jpg'}
          alt="Doctor Background"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="container mx-auto px-4 relative z-20">
          <div className="max-w-2xl text-white">
            <h1 className="text-5xl font-bold mb-4">د. {settings?.doctor.name}</h1>
            <p className="text-xl">{settings?.doctor.title}</p>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section className="container mx-auto px-4">
        <div className="relative bg-gradient-to-br from-blue-50 via-white to-cyan-50 rounded-3xl shadow-2xl p-8 md:p-14 mb-16 overflow-hidden">
          {/* زخرفة خلفية */}
          <div className="absolute -top-24 -left-24 w-72 h-72 bg-gradient-to-br from-blue-200/30 to-cyan-200/30 rounded-full blur-3xl z-0"></div>
          <div className="absolute -bottom-24 -right-24 w-72 h-72 bg-gradient-to-tr from-purple-200/30 to-pink-200/30 rounded-full blur-3xl z-0"></div>

          <div className="relative z-10 flex flex-col md:flex-row items-center gap-12">
            {/* صورة الدكتور */}
            <div className="flex-shrink-0">
              <div className="w-44 h-44 md:w-56 md:h-56 rounded-full overflow-hidden border-4 border-blue-400 shadow-lg bg-white">
                <img
                  src={settings?.doctor.image || '/doctor-placeholder.jpg'}
                  alt={settings?.doctor.name}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>

            {/* بيانات الدكتور */}
            <div className="flex-1 text-center md:text-right">
              <h2 className="text-3xl md:text-4xl font-extrabold bg-gradient-to-r from-blue-700 via-cyan-600 to-purple-600 bg-clip-text text-transparent mb-2">
                {settings?.doctor.name}
              </h2>
              <p className="text-lg text-blue-500 font-semibold mb-4">{settings?.doctor.title}</p>
              <div className="flex flex-wrap justify-center md:justify-end gap-6 mb-6">
                <div className="flex items-center gap-2">
                  <Clock className="w-6 h-6 text-blue-400" />
                  <span className="text-gray-700 font-medium">{settings?.doctor.statistics.experienceYears} سنة خبرة</span>
                </div>
                <div className="flex items-center gap-2">
                  <Award className="w-6 h-6 text-green-400" />
                  <span className="text-gray-700 font-medium">{settings?.doctor.statistics.successfulCases}+ حالة ناجحة</span>
                </div>
                <div className="flex items-center gap-2">
                  <Star className="w-6 h-6 text-yellow-400" />
                  <span className="text-gray-700 font-medium">{settings?.doctor.statistics.satisfactionRate}% رضا المرضى</span>
                </div>
              </div>
              <blockquote className="italic text-gray-600 text-lg leading-relaxed max-w-2xl mx-auto mb-4">
                “{settings?.doctor.about}”
              </blockquote>
              {/* المؤهلات أو الشهادات */}
              <div className="flex flex-wrap gap-2 justify-center md:justify-end mb-4">
                {settings?.doctor.qualifications?.map((q, i) => (
                  <span key={i} className="bg-blue-100 text-blue-700 px-3 py-1 rounded-full text-sm font-medium shadow-sm">
                    {q}
                  </span>
                ))}
              </div>
              <Button onClick={() => navigate('/book-appointment')} className="mt-2 text-lg font-semibold px-8 py-3 rounded-xl bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white shadow-lg hover:shadow-xl transition-all duration-300">
                <Calendar className="w-5 h-5 ml-2" />
                احجز موعدك الآن
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="bg-gray-50 py-20">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold text-center mb-12">لماذا تختارنا؟</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="bg-white p-6 rounded-lg text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Star className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold mb-2">خبرة عالية</h3>
              <p className="text-gray-600">
                فريق من الأطباء المتخصصين ذوي الخبرة الطويلة في مجال طب الأسنان
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold mb-2">أحدث التقنيات</h3>
              <p className="text-gray-600">
                نستخدم أحدث الأجهزة والتقنيات العالمية في مجال طب الأسنان
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Users className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold mb-2">رعاية شخصية</h3>
              <p className="text-gray-600">
                نقدم رعاية مخصصة لكل مريض مع خطة علاج فردية تناسب احتياجاته
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Calendar className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold mb-2">مواعيد مرنة</h3>
              <p className="text-gray-600">
                نوفر مواعيد مرنة تناسب جدولك مع إمكانية الحجز السريع
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About; 